import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Collapse,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { parseDateTime } from 'utils/datetime';
import PaginationData from 'types/models/PaginationData';
import Order from 'types/models/Order';
import { ConsumptionReportPayloadData } from 'types/forms/ConsumptionsReportFormProps';
import ConsumptionsReportForm from 'forms/ConsumptionsReportForm';
import { getReportingOrdersTotal, getReportingOrders, setOrdersToBilling } from 'services/orderServices';
import OrderDetail from 'types/models/OrderDetail';
import 'styles/pages/BillingOrdersPage.scss';
import { parsePaymentMethod } from 'utils/report';
import { getPaymentIdsByMethod } from 'services/paymentsServices';
import { valueToCurrencyFormat } from 'utils/parseCurrency';
import OrderBillingStatuses from 'types/enums/OrderBillingStatuses';
import InfoBanner from 'components/InfoBanner';
import { formatMoneyAuto } from 'utils/formatMoney';
import { formatPhone } from 'utils/formatPhone';
import ByMethodPaymentResult from 'types/models/ByMethodPaymentResult';
import AppContext from 'contexts/AppContext';

interface PageSelectedOrders {
  page: number;
  selectedOrderIds: number[];
  selectCurrentPageSwitchIsSelected: boolean;
}

export default (): JSX.Element => {
  /*
  Lógica de selección de órdenes
----- pagina actual
- selecciono todos los de la página y los guardo en un array
- si deselecciona alguno lo quito del array de seleccionados 
-- Enviar array de checkedOrdersToBilling, termino haciendo whereIn('id', $request->checkedOrdersToBilling)
 
----- todas
- si marca esta opción entonces dejo guardado en una variable que seleccionó "TODAS"
- si está marcada esta opción y en medio de la navegación deselecciona alguna, entonces la guardo en una variable
  uncheckedOrdersToBilling. Al backend le envío currentFormData y uncheckedOrdersToBilling, por lo que tengo que 
  agregar whereNotIn('id', $request->uncheckedOrdersToBilling)

  [
  {page: 1, selectedOrders: }
  ]

----- individual 
- Por cada orden seleccionada la agrego al array checkedOrderBillings
- Validar que ambos switchs estén desactivados
-- Enviar array de checkedOrdersToBilling, termino haciendo whereIn('id', $request->checkedOrdersToBilling)

// */
  const { setIsLoading, isLoading } = React.useContext(AppContext);
  const [currentFormData, setCurrentFormData] = React.useState<ConsumptionReportPayloadData | null>(null);
  const [total, setTotal] = React.useState<number | null>(null);
  const [totalToBilling, setTotalToBilling] = React.useState<number | null>(null);
  const [paginationOrders, setPaginationOrders] = React.useState<PaginationData<Order> | null>(null);
  const [openCollapseDetail, setOpenCollapseDetail] = React.useState(0);
  const paymentIdsForSearch = React.useRef<ByMethodPaymentResult[]>([]);

  const [pageSelectedOrders, setPageSelectedOrders] = React.useState<PageSelectedOrders[]>([]);
  const [uncheckedOrderBillingIds, setUncheckedOrderBillingIds] = React.useState<number[]>([]);
  const [selectAllOrdersSwitch, setSelectAllOrdersSwitch] = React.useState<boolean>(false);
  const [bannerMessage, setBannerMessage] = React.useState('');
  const [infoBannerResult, setInfoBannerResult] = React.useState<boolean>(false);

  const { t } = useTranslation();

  const loadData = async (page?: number) => {
    if (!currentFormData) return;
    setIsLoading(true);
    try {
      const { cloverPointIds, isCashPayment, isCardPayment, isQrPayment, ...consumptionsReportPayload } =
        currentFormData;
      const paymentsMethodsCollection = [isCashPayment, isCardPayment, isQrPayment];
      consumptionsReportPayload.orderBillingStatusId = OrderBillingStatuses.Unbilled;

      if (
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === false)) ||
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === true))
      ) {
        const getTotal = await getReportingOrdersTotal(consumptionsReportPayload);
        const getPaginationOrders = await getReportingOrders(consumptionsReportPayload, page);
        if (getPaginationOrders) {
          setPaginationOrders(getPaginationOrders);
        }
        setTotal(getTotal !== null ? getTotal : 0);
      } else {
        const result = await getPaymentIdsByMethod(
          {
            commerceIds: consumptionsReportPayload.commerceIds,
            cloverPointIds: cloverPointIds,
            isCardPayment: isCardPayment,
            isCashPayment: isCashPayment,
            isQrPayment: isQrPayment,
          },
          currentFormData.dateFrom,
          currentFormData.dateTo,
        );

        const allPaymentIdsPerMethods = page && page >= 1 ? paymentIdsForSearch.current : result;
        paymentIdsForSearch.current = allPaymentIdsPerMethods;
        consumptionsReportPayload.paymentIds = allPaymentIdsPerMethods;

        if (consumptionsReportPayload.paymentIds && consumptionsReportPayload.paymentIds.length === 0) {
          setPaginationOrders({
            currentPage: 0,
            data: [],
            perPage: 0,
            total: 0,
          });
          setTotal(0);
        } else {
          const getTotal = await getReportingOrdersTotal(consumptionsReportPayload);
          const getPaginationOrders = await getReportingOrders(consumptionsReportPayload, page);
          if (getPaginationOrders) {
            setPaginationOrders(getPaginationOrders);
          }
          setTotal(getTotal !== null ? getTotal : 0);
        }
      }
    } catch (error) {
      console.log('error loadData', error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (paginationOrders) {
      setTotal(null);
      setPaginationOrders(null);
      setUncheckedOrderBillingIds([]);
      setPageSelectedOrders([]);
      setSelectAllOrdersSwitch(false);
      setTotalToBilling(null);
    }
    if (paymentIdsForSearch.current.length > 0) {
      paymentIdsForSearch.current = [];
    }
    loadData();
  }, [currentFormData]);

  const handleCollapse = (id: number) => {
    if (openCollapseDetail === id) {
      setOpenCollapseDetail(0);
    } else {
      setOpenCollapseDetail(id);
    }
  };

  const getPaymentMethodText = (order: Order) => {
    if (order.refundOfOrder) {
      return t('consumptionsReport-page-paymentMethod-refund', { orderId: order.refundOfOrder });
    }

    return parsePaymentMethod(t, order);
  };

  const getCurrentPageIndex = (): number => {
    return pageSelectedOrders.findIndex((item) => item.page === paginationOrders?.currentPage);
  };

  const enabledActionButtons = (): boolean => {
    // se va a deshabilitar el botón cuando no tenga seleccionada ninguna orden
    const noSelectedOrders = pageSelectedOrders.every((page) => page.selectedOrderIds.length === 0);
    if (!selectAllOrdersSwitch && noSelectedOrders) return true;

    if (isLoading) return true;

    return false;
  };

  const orderIsSelected = (orderId: number): boolean => {
    if (selectAllOrdersSwitch) {
      // Si el switch de "todas las ordenes" está marcada entonces devolvemos true
      // Pero si la orden está dentro de los uncheckedOrderBillingIds, significa que se marcan todas las órdenes
      // menos las que estén dentro de ese array
      if (uncheckedOrderBillingIds.includes(orderId)) return false;
      return true;
    }

    // Si el switch de "todas las ordenes" destá desmarcada, entonces buscamos si la orden está seleccionada
    // dentro de su paginado
    const currentPageIndex = getCurrentPageIndex();
    if (currentPageIndex !== -1) {
      const orders = pageSelectedOrders[currentPageIndex].selectedOrderIds;
      return orders.includes(orderId);
    }

    return false;
  };

  const selectCurrentPageSwitchIsSelected = (): boolean => {
    // Por cada paginado vemos si su botón de "seleccionar página actual" está marcado
    const currentPageIndex = getCurrentPageIndex();
    if (currentPageIndex !== -1) {
      return pageSelectedOrders[currentPageIndex].selectCurrentPageSwitchIsSelected;
    }

    return false;
  };

  const getRequestBodyToBillingOrders = () => {
    const body: any = {
      allOrdersIsSelected: selectAllOrdersSwitch,
      ...currentFormData,
      withoutOrders: [],
      selectedOrderIds: [],
      paymentIds: paymentIdsForSearch.current ?? [],
    };
    if (selectAllOrdersSwitch) {
      // Si el switch se "Seleccionar todas las órdenes" está activa entonces llenamos las órdenes a ignorar
      body.withoutOrders = uncheckedOrderBillingIds;
      return body;
    }

    pageSelectedOrders.forEach((page) => {
      body.selectedOrderIds.push(...page.selectedOrderIds);
    });

    return body;
  };

  const onCheckOrder = (orderId: number, checked: boolean) => {
    if (selectAllOrdersSwitch) {
      // Si al seleccionar una orden está marcado el swtich de "seleccionar todas las órdenes"
      // y la orden se está desmarcando significa que vamos a agregar esa orden al listado de órdenes para ignorar
      if (!checked) {
        const orderIds = [...uncheckedOrderBillingIds, orderId];
        setUncheckedOrderBillingIds(orderIds);
      }
    } else {
      const currentPageIndex = getCurrentPageIndex();
      if (currentPageIndex !== -1) {
        // Si ya existe la página, entonces trabajamos sobre esa
        let orders = pageSelectedOrders[currentPageIndex].selectedOrderIds;
        if (checked) {
          // Si el check es true, entonces hay que agregar al id, sino lo filtramos
          orders.push(orderId);
        } else {
          orders = orders.filter((id) => id !== orderId);
        }

        pageSelectedOrders[currentPageIndex].selectedOrderIds = [...orders];
        setPageSelectedOrders([...pageSelectedOrders]);
      } else {
        //si no existía la página, entonces la agregamos
        const item = {
          page: paginationOrders?.currentPage as number,
          selectedOrderIds: [orderId],
          selectCurrentPageSwitchIsSelected: selectCurrentPageSwitchIsSelected(),
        };
        setPageSelectedOrders([...pageSelectedOrders, item]);
      }
    }
  };

  const renderContent = (order: Order, index: number): JSX.Element => {
    return (
      <React.Fragment key={index}>
        <TableRow className={`${orderIsSelected(order.id) && 'selected-row'}`}>
          <TableCell component="th" scope="row">
            <Checkbox
              key={index}
              color="primary"
              checked={orderIsSelected(order.id)}
              value={orderIsSelected(order.id)}
              onChange={(value) => onCheckOrder(order.id, value.target.checked)}
              disabled={order.billingStatusId !== OrderBillingStatuses.Unbilled}
            />
          </TableCell>
          <TableCell component="th" scope="row">
            {order.id}
          </TableCell>
          <TableCell>{order.commerce?.name}</TableCell>
          <TableCell>{formatMoneyAuto(order.total)}</TableCell>
          <TableCell>{parseDateTime(order.localDatetime)}</TableCell>
          <TableCell>{order.guestEmail || '-'}</TableCell>
          <TableCell>{order.phone ? formatPhone(order.phone) : '-'}</TableCell>
          <TableCell>{getPaymentMethodText(order)}</TableCell>
          <TableCell>
            {order?.firstDataPayment?.ccLastFourDigits
              ? `**** **** **** ${order.firstDataPayment.ccLastFourDigits}`
              : order?.cloverDataPayment?.cardLastFourDigits
              ? `**** **** **** ${order.cloverDataPayment.cardLastFourDigits}`
              : order?.izipayPayment?.cardNumber
              ? `**** **** **** ${order?.izipayPayment?.cardNumber.substring(
                  order?.izipayPayment?.cardNumber.length - 4,
                )}`
              : 'No Aplica'}
          </TableCell>
          <TableCell>
            {order.event ? `${order.event.name} - ${parseDateTime(order.event.eventStart)}` : 'Sin especificar'}
          </TableCell>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => handleCollapse(order.id)}>
              {order.id === openCollapseDetail ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell>
            {order?.cloverDataPayment?.device
              ? `${order?.cloverDataPayment?.device.description}, Serial: ${order?.cloverDataPayment.device.serial}`
              : order?.fiservQrDataPayment?.device
              ? `${order?.fiservQrDataPayment?.device.description}, Serial: ${order?.fiservQrDataPayment?.device.serial}`
              : 'No Aplica'}
          </TableCell>
          <TableCell>{order.delivery ?? 'No Aplica'}</TableCell>
          <TableCell>
            {order.billingStatusId === OrderBillingStatuses.BillingSuccess
              ? t('consumptionsReport-page-invoice-process-success')
              : '-'}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={order.id === openCollapseDetail} timeout="auto" unmountOnExit={true}>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom={true} component="div">
                  {t('consumptionsReport-page-detail')}
                </Typography>
                <Table size="small" aria-label="details">
                  <TableHead className="table-header">
                    <TableRow>
                      <TableCell>{t('consumptionsReport-page-product')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-amount')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-clothingSizeType')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-unitPrice')}</TableCell>
                      <TableCell>{t('consumptionsReport-page-totalProduct')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.details.map((detail: OrderDetail) => (
                      <TableRow key={detail.id}>
                        <TableCell component="th" scope="row">
                          {detail.product}
                        </TableCell>
                        <TableCell>{detail.quantity}</TableCell>
                        <TableCell>{detail.clothinSizeType?.description ?? '-'}</TableCell>
                        <TableCell>{formatMoneyAuto(detail.price)}</TableCell>
                        <TableCell>{formatMoneyAuto(detail.totalAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <ConsumptionsReportForm
        isForBillingOrders={true}
        currentFormData={currentFormData}
        setCurrentFormData={setCurrentFormData}
      />
      <>
        <InfoBanner
          message={bannerMessage}
          result={infoBannerResult}
          onClose={() => {
            setInfoBannerResult(false);
            setBannerMessage('');
          }}
        />
        {paginationOrders && paginationOrders.data.length > 0 && (
          <div className="order-billings-select-switchs-container">
            <FormControlLabel
              control={
                <Switch
                  checked={selectCurrentPageSwitchIsSelected()}
                  onChange={(event) => {
                    const value = event.target.checked;
                    const data = [...pageSelectedOrders];
                    const currentPageIndex = pageSelectedOrders.findIndex(
                      (item) => item.page === paginationOrders.currentPage,
                    );

                    if (currentPageIndex !== -1) {
                      const orderIds = value ? paginationOrders.data.map((order) => order.id) : [];
                      data[currentPageIndex] = {
                        page: paginationOrders.currentPage,
                        selectedOrderIds: orderIds,
                        selectCurrentPageSwitchIsSelected: value,
                      };
                      setPageSelectedOrders([...data]);
                    } else {
                      const orderIds = paginationOrders.data.map((order) => order.id);
                      const item = {
                        page: paginationOrders.currentPage,
                        selectedOrderIds: orderIds,
                        selectCurrentPageSwitchIsSelected: value,
                      };
                      setPageSelectedOrders([...pageSelectedOrders, item]);
                    }

                    if (value) {
                      // Si seleccionamos el switch de "seleccionar página actual" entonces  desactivamos el
                      // switch de "seleccionar todas las órdenes" y vaciamos el listado de órdenes ignoradas
                      setSelectAllOrdersSwitch(false);
                      setUncheckedOrderBillingIds([]);
                    }
                  }}
                />
              }
              label={t('order-billing-form-select-current-page')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={selectAllOrdersSwitch}
                  onChange={(event) => {
                    const value = event.target.checked;

                    if (!value) {
                      // Si se desmarcó el switch de "marcar todas las órdenes" entonces reiniciamos
                      // el listado de órdenes
                      setUncheckedOrderBillingIds([]);
                    }

                    // Si se marca el switch de "marcar todas las órdenes" entonces reiniciamos el paginado
                    // con sus órdenes seleccionadas
                    setPageSelectedOrders([]);
                    setSelectAllOrdersSwitch(value);
                  }}
                />
              }
              label={t('order-billing-form-select-all-orders')}
            />
          </div>
        )}
        <TableContainer className="table-container" component={Paper}>
          <Table>
            <TableHead className="table-header">
              <TableRow>
                <TableCell></TableCell>
                <TableCell>{t('consumptionsReport-page-order')}</TableCell>
                <TableCell>{t('consumptionsReport-page-commerce')}</TableCell>
                <TableCell>{t('consumptionsReport-page-total')}</TableCell>
                <TableCell>{t('consumptionsReport-page-createdDate')}</TableCell>
                <TableCell>{t('consumptionsReport-page-guestEmail')}</TableCell>
                <TableCell>{t('consumptionsReport-page-phone')}</TableCell>
                <TableCell>{t('consumptionsReport-page-paymentMethod')}</TableCell>
                <TableCell>{t('consumptionsReport-page-lastFourDigits')}</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>{t('consumptionsReport-page-detail')}</TableCell>
                <TableCell>{t('consumptionsReport-page-clover-point')}</TableCell>
                <TableCell>{t('consumptionsReport-page-delivery')}</TableCell>
                <TableCell>{t('consumptionsReport-page-invoice-process')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginationOrders?.data && paginationOrders?.data.length > 0 ? (
                paginationOrders?.data.map((order: Order, index) => renderContent(order, index))
              ) : (
                <TableRow>
                  <TableCell>{t('consumptionsReport-page-noOperations')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginationOrders && (
          <>
            <TableRow>
              <TableCell>{`${t('consumptionsReport-page-operationsTotal')}: ${
                total ? valueToCurrencyFormat(total) : 0
              }`}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{`${t('consumptionsReport-page-operationsTotalToBilling')}: ${
                totalToBilling ? valueToCurrencyFormat(totalToBilling) : 0
              }`}</TableCell>
            </TableRow>
            {paginationOrders && paginationOrders.data.length > 0 && (
              <TableRow>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={enabledActionButtons()}
                    onClick={async () => {
                      const body = getRequestBodyToBillingOrders();
                      const { ...consumptionsReportPayload } = currentFormData;

                      const getTotal = await getReportingOrdersTotal({ ...consumptionsReportPayload, ...body });
                      setTotalToBilling(getTotal !== null ? getTotal : 0);
                    }}
                  >
                    {t('order-billing-form-calculate-total-to-billing')}
                  </Button>
                </TableCell>
              </TableRow>
            )}

            <TablePagination
              component="div"
              count={paginationOrders.total}
              page={paginationOrders.currentPage - 1}
              onPageChange={(_, page: number) => {
                loadData(page + 1);
              }}
              rowsPerPage={paginationOrders.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />

            <Box marginTop="1rem">
              <Button
                className="btn-50"
                disabled={enabledActionButtons()}
                variant="contained"
                color="primary"
                type="submit"
                onClick={async () => {
                  const body = getRequestBodyToBillingOrders();
                  const { ...consumptionsReportPayload } = currentFormData;

                  setIsLoading(true);
                  try {
                    const result = await setOrdersToBilling({ ...consumptionsReportPayload, ...body });

                    if (result) {
                      setTotal(null);
                      setPaginationOrders(null);
                      setUncheckedOrderBillingIds([]);
                      setPageSelectedOrders([]);
                      setSelectAllOrdersSwitch(false);
                      setTotalToBilling(null);
                    }

                    setInfoBannerResult(true);
                    setBannerMessage('Las órdenes se marcaron correctamente');
                  } catch (error: any) {
                    if (error.error && error.error_code === 400) {
                      setInfoBannerResult(false);
                      setBannerMessage(error.error);
                    }
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                {t('order-billing-form-set-orders-to-billing-button')}
              </Button>
            </Box>
          </>
        )}
      </>
    </>
  );
};
